import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha, tokens } from '../../theme/tokens';
import {
  applyOpenTarget,
  resolvePushOpenTarget,
} from '../../utils/alertOpen';
import { alertKindLabel } from '../../utils/labels';

function pushFrom() {
  return `${window.location.pathname}${window.location.search}`;
}

const KIND_CTA = {
  standard: 'View',
  banner: 'Read',
  audio: 'Listen',
  video: 'Watch',
  actionable: 'Respond',
};

function openFromPushPayload(payload, navigate) {
  const data = payload?.data || {};
  const actionId = typeof payload?.actionId === 'string' ? payload.actionId : '';
  if (actionId && actionId !== 'open' && actionId !== 'later' && data.id) {
    navigate(
      `/alerts?action=${encodeURIComponent(actionId)}&nid=${encodeURIComponent(String(data.id))}`,
      { state: { from: pushFrom() } },
    );
    return;
  }

  const path =
    typeof payload?.path === 'string'
      ? payload.path
      : typeof payload?.url === 'string'
        ? payload.url
        : '';

  if (payload?.external || /^https?:\/\//i.test(path)) {
    const href =
      typeof payload?.url === 'string' && /^https?:\/\//i.test(payload.url)
        ? payload.url
        : path;
    if (/^https?:\/\//i.test(href)) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
  }

  if (path.startsWith('/') && !path.startsWith('//')) {
    navigate(path, { state: { from: pushFrom() } });
    return;
  }

  const fromData = resolvePushOpenTarget(data);
  if (applyOpenTarget(fromData, navigate, { from: pushFrom() })) return;
  navigate('/alerts', { state: { from: pushFrom() } });
}

export function PushToastListener() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    function onMessage(event) {
      const payload = event.data;
      if (!payload || typeof payload !== 'object') return;

      if (payload.type === 'NOTIFICATION_CLICK') {
        openFromPushPayload(
          {
            ...payload,
            actionId: payload.actionId || undefined,
          },
          navigate,
        );
        return;
      }

      if (payload.type !== 'PUSH_RECEIVED') return;

      const kind = payload.kind || payload.data?.kind || 'standard';
      const thumb =
        (typeof payload.image === 'string' && payload.image) ||
        (typeof payload.data?.imageUrl === 'string' && payload.data.imageUrl) ||
        (typeof payload.icon === 'string' &&
        !String(payload.icon).endsWith('/pwa-192x192.png')
          ? payload.icon
          : null) ||
        null;

      console.log(
        'PRASAAR_PUSH page received',
        JSON.stringify({
          title: payload.title,
          body: payload.body,
          kind,
          path: payload.path,
          hasImage: Boolean(thumb),
        }),
      );

      enqueueSnackbar(payload.body || 'New alert', {
        variant: 'default',
        autoHideDuration: kind === 'banner' ? 9000 : 6500,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        content: (snackKey, message) => (
          <Box
            sx={{
              backgroundColor: tokens.surface,
              color: tokens.ink,
              borderRadius: 3,
              p: kind === 'banner' ? 0 : 2,
              minWidth: 0,
              width: { xs: '100%', sm: 'auto' },
              maxWidth: {
                xs: 'calc(100% - 24px - var(--safe-left) - var(--safe-right))',
                sm: kind === 'banner' ? 420 : 380,
              },
              boxShadow: `0 18px 40px ${alpha(tokens.ink, 0.22)}`,
              border: `1px solid ${tokens.border}`,
              borderLeft: kind === 'banner' ? 'none' : `5px solid ${tokens.brandFill}`,
              overflow: 'hidden',
            }}
          >
            <Stack spacing={kind === 'banner' ? 0 : 1.25}>
              {thumb ? (
                <Box
                  component="img"
                  src={thumb}
                  alt=""
                  sx={{
                    width: '100%',
                    maxHeight: kind === 'banner' ? { xs: 180, sm: 200 } : 140,
                    objectFit: 'cover',
                    borderRadius: kind === 'banner' ? 0 : 2,
                    display: 'block',
                  }}
                />
              ) : null}
              <Stack spacing={1.25} sx={{ p: kind === 'banner' ? 2 : 0 }}>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: tokens.inkMuted,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Prasaar
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.25,
                      mt: 0.25,
                      wordBreak: 'break-word',
                      fontSize: { xs: '1.05rem', sm: '1.15rem' },
                    }}
                  >
                    {payload.title || 'New notification'}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={alertKindLabel(kind)}
                  sx={{
                    bgcolor: tokens.primarySoft,
                    color: tokens.brandInk,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                />
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  color: tokens.inkMuted,
                  lineHeight: 1.6,
                  wordBreak: 'break-word',
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                }}
              >
                {message}
              </Typography>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
                  {kind === 'audio'
                    ? 'Open alerts to play audio'
                    : kind === 'video'
                      ? 'Open alerts to watch'
                      : kind === 'actionable'
                        ? 'Response needed'
                        : kind === 'banner'
                          ? 'Headline alert'
                          : 'Open the full alert'}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    closeSnackbar(snackKey);
                    openFromPushPayload(payload, navigate);
                  }}
                  sx={{
                    minWidth: 88,
                    fontWeight: 700,
                    boxShadow: 'none',
                  }}
                >
                  {KIND_CTA[kind] || 'View'}
                </Button>
              </Stack>
              </Stack>
            </Stack>
          </Box>
        ),
      });
    }

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [closeSnackbar, enqueueSnackbar, navigate]);

  return null;
}
